// Display labels for these lists live in src/i18n/strings.ts
// (`topic.<value>`, `mood.<value>`), so they can be shown in EN/FI/SV.
//
// Must stay in lockstep with _allowed_topics() in
// supabase/migrations/0003_suppression_functions.sql — the server is the
// real enforcement point, this list is only for rendering the UI.
export const TOPIC_TAGS = [
  { value: 'schoolwork', emoji: '🏫' },
  { value: 'friends', emoji: '👫' },
  { value: 'family', emoji: '🏠' },
  { value: 'sleep', emoji: '🌙' },
  { value: 'health', emoji: '🤒' },
  { value: 'exams', emoji: '📝' },
  { value: 'other', emoji: '💭' },
] as const;

// Emoji follow the zencera design system's score faces (😔 😕 😐 🙂 😊);
// topic emoji are display-only and never sent to the server.
export const MOOD_SCALE = [
  { value: 1, emoji: '😔' },
  { value: 2, emoji: '😕' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '🙂' },
  { value: 5, emoji: '😊' },
] as const;

// The safety-net message (shown on every check-in screen regardless of what's
// selected — design doc Section 4.6) now lives in src/i18n/strings.ts as
// 'safety.message' in EN/FI/SV. Its counsellor name/room is still a
// placeholder ("Room TBD") that must not ship as-is.

// The "Something else" follow-up (Release 4b, corrected in 4c): a closed,
// global vocabulary, curated per school grade by its head of school
// (grade_something_else) — this list is every code that CAN exist, not
// what one class is offered (that comes back per-session from
// get_active_session's concern_phrases). Must stay in lockstep with
// _concern_phrase_codes() in supabase/migrations/0022_release4c.sql (0021's
// 6 placeholder codes were replaced with the brief's real 7). Labels live
// in i18n as `concern.<value>`.
export const CONCERN_CODES = [
  { value: 'left_out_or_bullied', emoji: '🚫' },
  { value: 'online_and_phone', emoji: '📱' },
  { value: 'body_or_looks', emoji: '🪞' },
  { value: 'the_future', emoji: '🔭' },
  { value: 'hobbies_or_sport', emoji: '⚽' },
  { value: 'money', emoji: '💶' },
  { value: 'unspecified', emoji: '💭' },
] as const;

// Topic & follow-up check-in (migrations 0024/0025): the six SWIPE research
// areas (spec §14.1), platform-fixed and admin-managed (active_categories()
// is the real, live list — this is only the emoji each one shows; labels
// live in i18n as `area.<value>`). Must stay in lockstep with the seed in
// supabase/migrations/0025_swipe_content_foundation.sql.
export const AREA_CODES = [
  { value: 'feel', emoji: '🙂' },
  { value: 'connect', emoji: '🤝' },
  { value: 'grow', emoji: '🌱' },
  { value: 'become', emoji: '🧭' },
  { value: 'contribute', emoji: '🤲' },
  { value: 'safe_ground', emoji: '🛡️' },
] as const;

export type TopicValue = (typeof TOPIC_TAGS)[number]['value'];
export type MoodValue = (typeof MOOD_SCALE)[number]['value'];
export type ConcernValue = (typeof CONCERN_CODES)[number]['value'];
export type AreaValue = (typeof AREA_CODES)[number]['value'];

// Accessibility statement feedback channel (Act on the Provision of Digital
// Services 306/2019: electronic channel, reply within 14 days).
// PLACEHOLDER — set the real monitored address before go-live. While null,
// the statement shows a visible "to be added" note instead of a mailto link.
export const A11Y_FEEDBACK_EMAIL: string | null = null;
export const A11Y_STATEMENT_DATE = '2026-10-07';
